import React, {
  useState, useRef, useEffect, CSSProperties, useMemo,
} from 'react';

import {
  Table, Icon, Popup, Button, Message,
} from 'semantic-ui-react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import api from 'api';
import ReduxActions from 'actions';
import { Other } from 'simplydo/interfaces';
import useTheme from 'theme/useTheme';

import { LargeCheckbox } from 'components/lib/UI';
import TableSettings from './TableSettings';
import ActionDropdown, { ActionDropdownProps } from './ActionDropdown';
import DataRow from './DataRow';
import {
  Column, ColumnOption, ColumnSetting,
} from './types';

const actionKey = '__actions';
const minCellWidth = 75;

export type Columns<ItemType> = ColumnOption<ItemType>[]

export type ConfigurableTableProps<ItemType, T extends keyof ItemType = keyof ItemType> = {
  keyExtractor?: (item: ItemType) => ItemType[T];
  tableKey: string;
  data: ItemType[];
  columns: Columns<ItemType>;
  onRowClick?: (item: ItemType, event: Event) => void;
  getRowStyle?: (item: ItemType) => CSSProperties;
  getCellStyle?: (cell: ItemType[keyof ItemType], item: ItemType) => CSSProperties;
  onSelect?: (item: ItemType | null, checked: boolean) => void;
  preventSelectAll?: boolean;
  selectedKeys?: ItemType[T][];
  draggable?: boolean;
  actions?: ActionDropdownProps<ItemType>['actions'];
  sort?: { key: keyof ItemType, direction: 'asc' | 'desc' };
  onSort?: (key: keyof ItemType) => void;
  restrictOverflow?: boolean,
  stackable?: boolean,
  wrapperStyle?: CSSProperties,
  containerStyle?: CSSProperties,
  tableStyle?: CSSProperties,
  basic?: boolean
  headerStyle?: CSSProperties,
};

const TableWrapper = styled.div`
  margin-top: ${({ theme }) => (theme.sizes.isMobile ? '10px' : '0')};
  margin-bottom: 10px;
  position: relative;
  max-width: 100%;
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const OverflowContainer = styled.div<{ restrictOverflow?: boolean }>`
  overflow: auto;
  ${({ restrictOverflow }) => !restrictOverflow && 'min-height: 600px;'}
  border-radius: 4px;
  max-width: 100%;
  flex: 1 1 50vh;

  &.scrollable {
    &::after {
      z-index: 300;
      content: '';
      width: 100%;
      position: absolute;
      height: 100%;
      top: 0;
      border-radius: 4px;
      pointer-events: none;
      transition: box-shadow .2s;
      box-shadow: inset -10px 0 10px -5px rgb(0 0 0 / 15%), inset 10px 0 10px -5px rgb(0 0 0 / 15%);
    }

    &.scrollStart {
      &::after {
        box-shadow: inset -10px 0 10px -5px rgb(0 0 0 / 15%), inset 10px 0 10px -5px rgb(0 0 0 / 0%);
      }
    }
    &.scrollEnd {
      &::after {
        box-shadow: inset -10px 0 10px -5px rgb(0 0 0 / 0%), inset 10px 0 10px -5px rgb(0 0 0 / 15%);
      }
    }
  }
`;

const SettingsIcon = styled.div`
  z-index: 400;
  position: absolute;
  right: 2px;
  top: 2px;
  cursor: pointer;
  padding: 0 2px;
  border-radius: 4px;
  color: #767676;

  :hover {
    background: rgba(0,0,0,0.2);
  }
`;

const SizedHeaderCell = styled(Table.HeaderCell)`
  ${({ sortable }) => sortable && `
    &&&&:hover {
      cursor: pointer;
      background: #e9e9e9;
    }
  `}

  ${({ preferredwidth }) => (preferredwidth ? `
    // Adjust for padding in table cells;
    min-width: calc(${preferredwidth}px + 1.4em);
    width: calc(${preferredwidth}px + 1.4em);
  ` : `
    min-width: ${minCellWidth}px;
  `)}
`;

const ConfigurableTable = <ItemType extends Record<string, any>>({
  tableKey,
  data,
  columns,
  onRowClick,
  sort,
  onSort,
  onSelect,
  preventSelectAll,
  actions,
  draggable,
  selectedKeys = [],
  keyExtractor = (item: ItemType) => item._id,
  restrictOverflow,
  wrapperStyle,
  containerStyle,
  tableStyle,
  basic,
  headerStyle,
  stackable,
  getRowStyle,
  getCellStyle,
}: ConfigurableTableProps<ItemType>) => {
  const [settingsOpen, setSettingsOpen] = useState(false);

  const dispatch = useDispatch();
  const user = useSelector((state: { user: Other.IUserMe | null }) => state?.user);
  const savedColumns = useMemo(() => {
    if (!user) {
      return [];
    }
    const { tablePreferences = {} } = user;
    const { columns: userColumns = [] } = (tablePreferences ?? {})[tableKey] ?? {};
    return userColumns ?? [];
  }, [user, tableKey]);

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let effectElement: HTMLDivElement | null = null;
    const trackScroll = (e) => {
      const { scrollLeft, clientWidth } = e.target;
      const innerWidth = e.target.childNodes[0].clientWidth;

      if (scrollLeft === 0) {
        e.target.classList.add('scrollStart');
      } else {
        e.target.classList.remove('scrollStart');
      }

      if (innerWidth - clientWidth <= scrollLeft + 2) {
        e.target.classList.add('scrollEnd');
      } else {
        e.target.classList.remove('scrollEnd');
      }
    };

    if (containerRef.current) {
      effectElement = containerRef.current;
      const containerWidth = containerRef.current.clientWidth;
      const tableElement: HTMLTableElement = containerRef.current.childNodes[0] as HTMLTableElement;
      const tableWidth = tableElement.clientWidth;
      if (containerWidth < tableWidth) {
        containerRef.current.classList.add('scrollable');
        containerRef.current.classList.add('scrollStart');
      }

      containerRef.current.addEventListener('scroll', trackScroll);
    }

    return () => {
      if (effectElement) {
        effectElement.removeEventListener('scroll', trackScroll);
      }
    };
  }, [containerRef]);

  const hasActions = useMemo(() => !!actions, [actions]);
  const columnSettings = useMemo(() => {
    let allColumns = columns;
    if (hasActions) {
      allColumns = [...columns, {
        key: actionKey,
        name: 'Actions dropdown',
        width: 75,
        center: true,
      }];
    }

    return allColumns.map((column, i) => {
      const savedEntry = savedColumns.find((col) => col.key === column.key);
      return {
        ...column,
        name: column.name ?? column.key,
        enabled: savedEntry?.enabled ?? column.enabled ?? true,
        order: savedEntry?.order ?? i,
      };
    }).sort((a, b) => a.order - b.order);
  }, [columns, hasActions, savedColumns]);
  const enabledColumns = useMemo(() => columnSettings.filter((col) => col.enabled), [columnSettings]);

  const theme = useTheme();
  const selectable = !!onSelect && !theme.sizes.isMobile;
  const selectedItems = useMemo(() => data.filter((item) => selectedKeys.includes(keyExtractor(item))), [data, selectedKeys, keyExtractor]);

  const updateColumns = (newColumns: Column<ItemType>[] | null) => {
    // Filter props to only save the ones we actually need in the db
    const saveableColumns: ColumnSetting<ItemType>[] | null = newColumns?.map((col) => ({
      key: col.key,
      enabled: col.enabled,
      order: col.order,
    })) ?? null;
    const prevSettings = { ...columnSettings };

    // Apply immediately locally, but revert to previous if the api call fails
    dispatch(ReduxActions.user.updateTablePreferences(tableKey, { columns: saveableColumns }));
    api.users.updateTablePreferences(user?._id, tableKey, { columns: saveableColumns }, () => { }, () => {
      dispatch(ReduxActions.user.updateTablePreferences(tableKey, { columns: prevSettings }));
    });
  };

  const tableSettings = (
    <TableSettings
      open={settingsOpen}
      onClose={() => setSettingsOpen(false)}
      updateColumns={updateColumns}
      columns={columnSettings}
    />
  );

  if (enabledColumns.length === 0) {
    const text = data.length > 0 ? `There are ${data.length} results but all columns are disabled so there is no data to show.` : 'There are no results and all columns are disabled so there is no data to show.';

    return (
      <>
        {tableSettings}
        <Message icon warning>
          <Icon name='warning sign' />
          <Message.Content>
            <Message.Header>No columns enabled</Message.Header>
            <p>{text}</p>
            <Button
              onClick={() => setSettingsOpen(true)}
            >
              Edit table settings
            </Button>
          </Message.Content>
        </Message>
      </>
    );
  }

  return (
    <>
      {tableSettings}
      <TableWrapper style={wrapperStyle}>
        <Popup
          trigger={
            <SettingsIcon onClick={() => setSettingsOpen(true)}>
              <Icon size='small' name='cogs' color="grey" />
            </SettingsIcon>
          }
          content="Edit layout and change settings for this table."
        />
        <OverflowContainer ref={containerRef} restrictOverflow={restrictOverflow} style={containerStyle}>
          <Table compact selectable={!!onRowClick} style={tableStyle} stackable={stackable} unstackable={!stackable} basic={basic}>
            <Table.Header style={headerStyle}>
              <Table.Row>
                {selectable && !preventSelectAll
                  ? (<SizedHeaderCell preferredwidth="40">
                    <div style={{
                      display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center',
                    }}>
                      <LargeCheckbox
                        checked={selectedKeys.length > 0}
                        onChange={(e, checked) => {
                          e.preventDefault();
                          onSelect && onSelect(null, checked);
                        }}
                      />
                    </div>
                  </SizedHeaderCell>)
                  : (
                    <>
                      {selectable
                        ? <SizedHeaderCell preferredwidth="40" />
                        : null
                      }
                    </>
                  )}
                {enabledColumns.map((col) => (
                  <SizedHeaderCell
                    key={col.key}
                    preferredwidth={col.width}
                    sortable={col.sortable && onSort && sort}
                    onClick={() => col.sortable && onSort && onSort(col.key)}
                    collapsing={col.collapsing}
                    style={col.key === actionKey ? {
                      position: 'sticky',
                      right: 0,
                      background: '#f9fafb',
                      boxShadow: '-1px 0 1px #ddd',
                    } : {}}
                  >
                    {col.key === actionKey ? (
                      selectable && <ActionDropdown
                        actions={actions}
                        items={selectedItems}
                        type='multi'
                        user={user}
                      />) : (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'nowrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}>
                        {col.name}
                        {col.sortable && sort && sort.key === col.key
                          ? <Icon
                            name={`chevron circle ${sort.direction === 'asc' ? 'up' : 'down'}`}
                            style={{ marginTop: -9 }}
                          />
                          : null}
                      </div>
                    )}
                  </SizedHeaderCell>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.map((item, index) => (
                <DataRow
                  index={index}
                  key={keyExtractor(item)}
                  selected={selectedKeys.includes(keyExtractor(item))}
                  selectable={selectable}
                  item={item}
                  enabledColumns={enabledColumns}
                  onSelect={onSelect}
                  onRowClick={onRowClick}
                  actions={actions}
                  user={user}
                  draggable={draggable}
                  getRowStyle={getRowStyle}
                  getCellStyle={getCellStyle}
                />
              ))}
            </Table.Body>
          </Table>
        </OverflowContainer>
      </TableWrapper>
    </>
  );
};

export default React.memo(ConfigurableTable);
